import { theme } from 'aimo-ui';
import type { QuickAccessType } from 'navigation/ParkScreenQuickAccess';
import type { TabBarIndicator } from 'types/states';
import { create } from 'zustand';

export type UpdateInformation = {
  isMandatory: boolean;
  // Defers the account-restricted modal until the update screen is closed.
  openFailedSessions?: boolean;
} | null;

interface UiState {
  // Which sheet/modal is open, keyed by name; '' closes whatever is open.
  sheetModal: string;
  isAppStartupScreenOpen: boolean;
  updateInformation: UpdateInformation;
  isApproveTermsOpen: boolean;
  // Whether the ongoing-parking sheet should expand the next time a session
  // appears — a one-shot request, consumed by useOngoingSheetState, not a
  // record of whether the sheet is currently expanded.
  expandOngoingSheet: boolean;
  locationModalShown: boolean;
  skipInitialNotificationpermission: boolean;
  isSignUpFlow: boolean;
  tabBarIndicators: TabBarIndicator[];
  selectedParkScreenQuickAccessItem: QuickAccessType | undefined;
  accountTheme: string;
}

interface UiActions {
  setSheetModal: (modalName: string) => void;
  setIsAppStartupScreenOpen: (isOpen: boolean) => void;
  setUpdateInformation: (information: UpdateInformation) => void;
  setIsApproveTermsOpen: (isOpen: boolean) => void;
  setExpandOngoingSheet: (shouldExpand: boolean) => void;
  setLocationModalShown: (wasShown: boolean) => void;
  setSkipInitialNotificationpermission: (shouldSkip: boolean) => void;
  setIsSignUpFlow: (isSignUp: boolean) => void;
  setTabBarIndicators: (indicators: TabBarIndicator[]) => void;
  setSelectedParkScreenQuickAccessItem: (
    item: QuickAccessType | undefined
  ) => void;
  setAccountTheme: (color: string) => void;
}

// Deliberately not persisted to MMKV: every value here is per-run UI state, and
// none of the reactive vars this replaced were stored either.
export const useUiStore = create<UiState & UiActions>((set) => ({
  sheetModal: '',
  isAppStartupScreenOpen: false,
  updateInformation: null,
  isApproveTermsOpen: true,
  expandOngoingSheet: true,
  locationModalShown: false,
  skipInitialNotificationpermission: false,
  isSignUpFlow: false,
  tabBarIndicators: [],
  selectedParkScreenQuickAccessItem: undefined,
  accountTheme: theme.colors.lightBg.orange,

  setSheetModal: (modalName) => set({ sheetModal: modalName }),
  setIsAppStartupScreenOpen: (isOpen) =>
    set({ isAppStartupScreenOpen: isOpen }),
  setUpdateInformation: (information) =>
    set({ updateInformation: information }),
  setIsApproveTermsOpen: (isOpen) => set({ isApproveTermsOpen: isOpen }),
  setExpandOngoingSheet: (shouldExpand) =>
    set({ expandOngoingSheet: shouldExpand }),
  setLocationModalShown: (wasShown) => set({ locationModalShown: wasShown }),
  setSkipInitialNotificationpermission: (shouldSkip) =>
    set({ skipInitialNotificationpermission: shouldSkip }),
  setIsSignUpFlow: (isSignUp) => set({ isSignUpFlow: isSignUp }),
  setTabBarIndicators: (indicators) => set({ tabBarIndicators: indicators }),
  setSelectedParkScreenQuickAccessItem: (item) =>
    set({ selectedParkScreenQuickAccessItem: item }),
  setAccountTheme: (color) => set({ accountTheme: color }),
}));
